import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api from "@/lib/api";
import { ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plane, Plus } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const statusColor = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};

const DriverHolidays = () => {
  const { data, isLoading } = useSWR("/holidays", fetcher);
  const [form, setForm] = useState({
    start_date: "", end_date: "", holiday_type: "annual", reason: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) return toast.error("Start and end dates are required");
    setBusy(true);
    try {
      await api.post("/holidays", form);
      toast.success("Holiday request sent");
      setForm({ start_date: "", end_date: "", holiday_type: "annual", reason: "" });
      mutate("/holidays");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    }
    setBusy(false);
  };

  return (
    <div className="p-4 pt-6">
      <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Time off</div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-brand-text mt-1">Holidays</h1>

      <Card className="mt-4 p-4 rounded-2xl border-brand-border">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Request time off</div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs uppercase tracking-wider text-slate-500">From</Label>
              <Input type="date" required value={form.start_date} data-testid="hol-start"
                     onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-500">To</Label>
              <Input type="date" required value={form.end_date} data-testid="hol-end"
                     onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} /></div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Type</Label>
            <Select value={form.holiday_type} onValueChange={(v) => setForm((f) => ({ ...f, holiday_type: v }))}>
              <SelectTrigger data-testid="hol-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="annual">Annual leave</SelectItem>
                <SelectItem value="sick">Sick</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Reason (optional)</Label>
            <Textarea rows={2} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-brand-red hover:bg-brand-red-hover" data-testid="hol-submit">
            <Plus className="w-4 h-4 mr-1.5" /> Submit request
          </Button>
        </form>
      </Card>

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Your requests</div>
        {isLoading && <div className="text-sm text-slate-500">Loading…</div>}
        <div className="space-y-2" data-testid="driver-holidays-list">
          {(data || []).map((h) => (
            <Card key={h.id} className="p-3 rounded-xl border-brand-border flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{ddmmyyyy(h.start_date)} — {ddmmyyyy(h.end_date)}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{h.holiday_type}{h.reason ? ` · ${h.reason}` : ""}</div>
              </div>
              <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${statusColor[h.status] || ""}`}>{h.status}</Badge>
            </Card>
          ))}
          {!isLoading && (data || []).length === 0 && (
            <Card className="p-6 rounded-xl text-center text-xs text-slate-500">
              <Plane className="w-6 h-6 mx-auto mb-2 opacity-40" />No requests yet.
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverHolidays;
